import { useState } from 'react'

import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { SubmitButton } from '@/components/ui/submit-button'
import { ChatActionWithValue } from '../../models/insuranceApplication'

export interface ChatCardCheckboxProps {
  options: Array<{ label: string; value: string }>
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  columns: number
  readonly?: boolean
  defaultSelected?: string[]
  singleSelect?: boolean
  allowOther?: boolean
}

const OTHER_VALUE = '__other__'

export function ChatCardCheckbox({
  options,
  columns,
  handleSubmit,
  readonly,
  defaultSelected,
  singleSelect,
  allowOther,
}: ChatCardCheckboxProps) {
  const [selectedOptions, setSelectedOptions] = useState<Array<string>>(defaultSelected ?? [])
  const [otherText, setOtherText] = useState('')

  const isOtherSelected = selectedOptions.includes(OTHER_VALUE)

  function onToggle(value: string, checked: boolean) {
    if (singleSelect) {
      setSelectedOptions(checked ? [value] : [])
    } else {
      setSelectedOptions(checked ? [...selectedOptions, value] : selectedOptions.filter((v) => v !== value))
    }
  }

  function onSubmit() {
    const effectiveValues = selectedOptions.map((v) => (v === OTHER_VALUE ? otherText.trim() : v))
    const labels = effectiveValues.map((val) => options.find((o) => o.value === val)?.label ?? val)

    handleSubmit({
      content: {
        component: 'P',
        content: labels.join('\n'),
      },
      value: singleSelect ? effectiveValues[0] : effectiveValues,
    })
  }

  const isSubmitDisabled = readonly || !selectedOptions.length || (isOtherSelected && !otherText.trim())

  return (
    <>
      <div className={`grid grid-cols-${columns} items-end justify-end gap-2`}>
        {options.map((option) => (
          <CardCheckbox
            key={option.value}
            isChecked={selectedOptions.includes(option.value)}
            disabled={readonly}
            onChange={(checked) => onToggle(option.value, checked)}
          >
            {option.label}
          </CardCheckbox>
        ))}
        {allowOther && (
          <CardCheckbox
            key={OTHER_VALUE}
            isChecked={isOtherSelected}
            disabled={readonly}
            onChange={(checked) => onToggle(OTHER_VALUE, checked)}
          >
            Other
          </CardCheckbox>
        )}
      </div>
      {allowOther && isOtherSelected && (
        <input
          type="text"
          className="border-input bg-background ring-offset-background placeholder:text-ink-300 focus-visible:ring-ring mt-2 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
          placeholder="Enter your ELD provider"
          value={otherText}
          disabled={readonly}
          onChange={(e) => setOtherText(e.target.value)}
        />
      )}
      <div className="mt-2 flex items-center justify-end">
        <SubmitButton onClick={onSubmit} disabled={isSubmitDisabled} />
      </div>
    </>
  )
}
